import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Col, Form, Row } from 'antd'
import { includes, isUndefined, map, range } from 'lodash'
import PropTypes from 'prop-types'

import { getFilterOptions } from '../../../core/utils'

import {
  FormButtons,
  FormInput,
  FormSelect,
} from '../../../common/components/sections'
import {
  StyledPaddingBottomRow,
  StyledPaddingTopCol,
} from '../../../common/components/styled'
import { useFormDirty } from '../../../common/hooks'

import { PRIORITY_TASK_ALLOCATION_TYPE } from '../../constants'

const PriorityTaskCreateForm = ({ isLoading, onSubmit }) => {
  const [form] = Form.useForm()
  const [selectedAllocationType, setSelectedAllocationType] = useState(null)
  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const priorityOptions = useMemo(
    () => map(range(1, 5), value => ({ value, label: value })),
    [],
  )

  const allocationTypeOptions = getFilterOptions(
    PRIORITY_TASK_ALLOCATION_TYPE,
    'label',
    'value',
  )

  const isKpiVisible = useMemo(
    () =>
      includes(
        [
          PRIORITY_TASK_ALLOCATION_TYPE.TOP_200.value,
          PRIORITY_TASK_ALLOCATION_TYPE.TIER_ONE.value,
        ],
        selectedAllocationType,
      ),
    [selectedAllocationType],
  )

  const handleFormValuesChange = useCallback(
    changedValues => {
      if (!isUndefined(changedValues.allocationType)) {
        setSelectedAllocationType(changedValues.allocationType)

        form.setFieldsValue({
          kpi: undefined,
          customRequiredDishCount: undefined,
        })
      }
    },
    [form],
  )

  const handleSubmit = useCallback(
    values => {
      resetForm()
      onSubmit(values)
    },
    [onSubmit, resetForm],
  )

  useEffect(() => {
    resetForm()
  }, [resetForm])

  return (
    <Form
      form={form}
      name="createPriorityTask"
      onFinish={handleSubmit}
      autoComplete="off"
      onFieldsChange={setToDirty}
      initialValues={{}}
      onValuesChange={handleFormValuesChange}
    >
      <Row gutter={[16]}>
        <Col span={5}>
          <FormInput
            name="name"
            label="Task Name"
            labelAbove
            rules={[
              {
                required: true,
              },
            ]}
          />
        </Col>
      </Row>
      <Row gutter={[16]}>
        <Col span={5}>
          <FormInput
            name="description"
            label="Task Description"
            labelAbove
            rules={[
              {
                required: true,
              },
            ]}
          />
        </Col>
      </Row>
      <Row gutter={[16]}>
        <Col span={5}>
          <FormSelect
            name="allocationType"
            label="Allocation type"
            labelAbove
            options={allocationTypeOptions}
            rules={[
              {
                required: true,
              },
            ]}
          />
        </Col>
      </Row>
      <Row gutter={[16]}>
        <Col span={5}>
          <FormSelect
            name="priority"
            label="Priority"
            labelAbove
            options={priorityOptions}
            rules={[
              {
                required: true,
              },
            ]}
          />
        </Col>
      </Row>
      {isKpiVisible ? (
        <Row gutter={[16]}>
          <Col span={5}>
            <FormInput
              name="kpi"
              label="KPI"
              labelAbove
              isNumber
              rules={[
                {
                  required: true,
                },
              ]}
            />
          </Col>
        </Row>
      ) : (
        <Row gutter={[16]}>
          <Col span={5}>
            <FormInput
              name="customRequiredDishCount"
              label="Required dish count"
              labelAbove
              isNumber
              rules={[
                {
                  required: true,
                },
              ]}
            />
          </Col>
        </Row>
      )}

      <StyledPaddingBottomRow>
        <StyledPaddingTopCol span={4}>
          <FormButtons
            isDisabled={!isDirty}
            isLoading={isLoading}
            onReset={resetForm}
          />
        </StyledPaddingTopCol>
      </StyledPaddingBottomRow>
    </Form>
  )
}

PriorityTaskCreateForm.propTypes = {
  isLoading: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
}

PriorityTaskCreateForm.defaultProps = {
  isLoading: false,
}

export default PriorityTaskCreateForm
